import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import {
  ActiveDriverPause,
  DriverPauseType,
  DriverReceiveWorkingShiftEvent,
  DriverStateEvent,
  SessionType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export type PersonalTripStep =
  | 'idle'           // немає активної поїздки
  | 'form'           // введення одометра та фото для старту
  | 'pending-start'  // запит надіслано, чекаємо підтвердження
  | 'rejected-start' // запит на старт відхилено
  | 'active'         // поїздка активна, таймер іде
  | 'finish-form'    // введення одометра та фото для завершення
  | 'pending-finish'; // запит на завершення надіслано, чекаємо

@Injectable({ providedIn: 'root' })
export class PersonalTripService {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<PersonalTripStep>('idle');
  readonly step = this._step.asReadonly();

  private readonly _elapsedSeconds = signal(0);
  readonly elapsedSeconds = this._elapsedSeconds.asReadonly();

  private readonly _startRequestId = signal<string | null>(null);
  private readonly _finishRequestId = signal<string | null>(null);

  private readonly _cancelledIds = new Set<string>();

  private timerInterval: ReturnType<typeof setInterval> | null = null;

  readonly formattedTime = computed(() => {
    const s = this._elapsedSeconds();
    const hh = Math.floor(s / 3600).toString().padStart(2, '0');
    const mm = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  });

  // ─── Cross-component notifications ───────────────────────────────────────

  /**
   * Стріляють лише при реальних WS-подіях (DriverRequestConfirmed / DriverRequestRejected).
   * Не стріляють при ініціалізації з workingShiftData/DriverStateEvent.
   * Використовуються ApprovalNotificationService для умовного показу сповіщення.
   */
  private readonly _startConfirmed$ = new Subject<void>();
  readonly startConfirmed$ = this._startConfirmed$.asObservable();

  private readonly _finishConfirmed$ = new Subject<void>();
  readonly finishConfirmed$ = this._finishConfirmed$.asObservable();

  private readonly _finishRejected$ = new Subject<void>();
  readonly finishRejected$ = this._finishRejected$.asObservable();

  private readonly _startRejected$ = new Subject<void>();
  readonly startRejected$ = this._startRejected$.asObservable();

  // ─── Init ─────────────────────────────────────────────────────────────────

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTimer());

    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);

        if (this._step() === 'pending-start' && event.id === this._startRequestId()) {
          this._step.set('active');
          this.startTimer(0);
          this._startConfirmed$.next();
        }

        if (this._step() === 'pending-finish' && event.id === this._finishRequestId()) {
          this.stopTimer();
          this._step.set('idle');
          this._startRequestId.set(null);
          this._finishRequestId.set(null);
          this.sessionService.clearSessionCacheByType(SessionType.FinishPersonalTrip);
          this._finishConfirmed$.next();
        }
      });

    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);

        if (this._step() === 'pending-start' && event.id === this._startRequestId()) {
          this._step.set('rejected-start');
          this._startRejected$.next();
        }

        if (this._step() === 'pending-finish' && event.id === this._finishRequestId()) {
          this._step.set('active');
          this._finishRequestId.set(null);
          this.sessionService.clearSessionCacheByType(SessionType.FinishPersonalTrip);
          // Перезапускаємо таймер — він продовжується після відхилення завершення
          if (this.timerInterval === null) {
            this.startTimer(this._elapsedSeconds());
          }
          this._finishRejected$.next();
        }
      });

    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        this.handleDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Ініціалізація стану при відкритті сторінки.
   * Якщо стан вже встановлено (не 'idle') — нічого не робимо, таймер продовжується.
   */
  init(): void {
    if (this._step() !== 'idle') return;

    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
      return;
    }

    const wshData = this.sessionService.workingShiftData();
    if (wshData) {
      this.initFromWorkingShiftData(wshData);
    } else {
      this._step.set('form');
    }
  }

  /** Надіслати запит на початок особистої поїздки */
  async sendStartRequest(mileage: number, sessionId: string): Promise<void> {
    const wshData = this.sessionService.workingShiftData();
    if (!wshData) throw new Error('No working shift data');

    const payloadBytes = this.proto.encodeStartPersonalTripPayload(mileage);
    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.StartPersonalTrip,
      sessionId,
      payloadBytes,
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (!response.body?.length) throw new Error('CreateDriverRequest returned empty body');
    const parsedStart = this.proto.decodeCreateDriverRequestResponse(response.body);
    this._startRequestId.set(parsedStart.id);
    this._step.set('pending-start');
  }

  /** Скасувати запит на початок поїздки */
  async cancelStartRequest(): Promise<void> {
    const requestId = this._startRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }
    this.resetState();
    // Запит скасовано вручну — при наступному init() не підхоплювати сталу паузу з сервера,
    // оскільки _cancelledIds фільтрує за request.id, а не pause.id
    this._step.set('form');
  }

  /** Повернутися до форми після відхилення */
  retryStart(): void {
    this._startRequestId.set(null);
    this.sessionService.clearSessionCacheByType(SessionType.StartPersonalTrip);
    this._step.set('form');
  }

  /** Перейти до форми завершення поїздки */
  enterFinishForm(): void {
    this._step.set('finish-form');
  }

  /** Скасувати форму завершення, повернутися до активного стану */
  cancelFinishForm(): void {
    this._step.set('active');
  }

  /** Надіслати запит на завершення поїздки */
  async sendFinishRequest(mileage: number, sessionId: string): Promise<void> {
    const wshData = this.sessionService.workingShiftData();
    if (!wshData) throw new Error('No working shift data');

    const payloadBytes = this.proto.encodeFinishPersonalTripPayload(mileage);
    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.FinishPersonalTrip,
      sessionId,
      payloadBytes,
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (!response.body?.length) throw new Error('CreateDriverRequest returned empty body');
    const parsedFinish = this.proto.decodeCreateDriverRequestResponse(response.body);
    this._finishRequestId.set(parsedFinish.id);
    // Таймер зупиняємо під час очікування підтвердження завершення
    this.stopTimer();
    this._step.set('pending-finish');
  }

  resetState(): void {
    this.stopTimer();
    this._step.set('idle');
    this._startRequestId.set(null);
    this._finishRequestId.set(null);
    this._elapsedSeconds.set(0);
    this.sessionService.clearSessionCacheByType(SessionType.StartPersonalTrip);
    this.sessionService.clearSessionCacheByType(SessionType.FinishPersonalTrip);
    // _cancelledIds зберігаємо — потрібні для фільтрації запізнілих DriverStateEvents
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private initFromDriverStateEvent(event: DriverStateEvent): void {
    // Перевіряємо активну паузу типу PersonalTrip
    if (event.driverPause?.type === DriverPauseType.PersonalTrip
        && !this._cancelledIds.has(event.driverPause.id)) {
      this.applyActivePause(event.driverPause);
      return;
    }

    // Перевіряємо запит на завершення
    const finishRequest = event.requests.find(
      (r) => r.type === SessionType.FinishPersonalTrip && !this._cancelledIds.has(r.id),
    );
    if (finishRequest) {
      this._finishRequestId.set(finishRequest.id);
      this._step.set('pending-finish');
      return;
    }

    // Перевіряємо запит на старт
    const startRequest = event.requests.find(
      (r) => r.type === SessionType.StartPersonalTrip && !this._cancelledIds.has(r.id),
    );
    if (startRequest) {
      this._startRequestId.set(startRequest.id);
      this._step.set(startRequest.status === 'confirmed' ? 'active' : 'pending-start');
      if (startRequest.status === 'confirmed') this.startTimer(0);
      return;
    }

    this._step.set('form');
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    // Перевіряємо активну паузу типу PersonalTrip
    const personalPause = data.driverPause?.find(
      (p) => p.type === DriverPauseType.PersonalTrip && !this._cancelledIds.has(p.id),
    );
    if (personalPause) {
      this.applyActivePause(personalPause);
      return;
    }

    // Перевіряємо запит на завершення
    const finishRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.FinishPersonalTrip && !this._cancelledIds.has(r.id),
    );
    if (finishRequest) {
      this._finishRequestId.set(finishRequest.id);
      this._step.set('pending-finish');
      return;
    }

    // Перевіряємо запит на старт
    const startRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.StartPersonalTrip && !this._cancelledIds.has(r.id),
    );
    if (startRequest) {
      this._startRequestId.set(startRequest.id);
      this._step.set(startRequest.status === 'confirmed' ? 'active' : 'pending-start');
      if (startRequest.status === 'confirmed') this.startTimer(0);
      return;
    }

    this._step.set('form');
  }

  private applyActivePause(pause: ActiveDriverPause): void {
    if (pause.startedAt) {
      const elapsed = Math.floor(Date.now() / 1000) - Number(pause.startedAt.seconds);
      this.startTimer(Math.max(0, elapsed));
    } else {
      this.startTimer(0);
    }
    this._step.set('active');
  }

  private handleDriverStateEvent(event: DriverStateEvent): void {
    // Ініціалізуємо тільки якщо ще не готові; pending стани розбираються через
    // DriverRequestConfirmed / DriverRequestRejected — не перезаписуємо їх звідси
    if (this._step() === 'idle') {
      this.initFromDriverStateEvent(event);
      return;
    }

    // Синхронізація таймера з сервером під час активної поїздки
    const isTimerActive = this._step() === 'active' || this._step() === 'finish-form';
    if (isTimerActive && event.driverPause?.type === DriverPauseType.PersonalTrip && event.driverPause.startedAt) {
      const elapsed = Math.floor(Date.now() / 1000) - Number(event.driverPause.startedAt.seconds);
      const corrected = Math.max(0, elapsed);
      if (this._step() === 'active') {
        this.stopTimer();
        this._elapsedSeconds.set(corrected);
        this.startTimer(corrected);
      } else {
        this._elapsedSeconds.set(corrected);
      }
    }
  }

  private startTimer(initialSeconds: number): void {
    this.stopTimer();
    this._elapsedSeconds.set(initialSeconds);

    this.timerInterval = setInterval(() => {
      this._elapsedSeconds.update((s) => s + 1);
    }, 1_000);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
